import * as jsonpatch from 'fast-json-patch';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { bindActionCreators } from 'redux';
import { validateEmail } from '~/Helpers/Validators.helper';
import { ToastVariant, useNavigation, useToastMessage, useUser, logout } from '~/Hooks';
import { useRequest } from '~/Hooks/useRequest';
import { IProviderProfilePicture } from '~/Models/DTO/Provider.model';
import IUser from '~/Models/DTO/User.model';
import { requestClearError } from '~/store/app';
import { changePassword, setProfilePicture } from './helpers/APIs';
import { useProvider } from '~/Hooks/useProvider/useProvider';
import { useRequestUser } from '~/requests/userService';

export interface IValidationErrors {
  columnName: string;
  message: string;
}

export interface IChangePassword {
  old_password: string;
  password: string;
  password_confirmation: string;
}

export interface IChangePasswordPayload {
  user: IChangePassword;
}

export interface ISettingsSection {}

export enum SettingSectionEnum {
  USER_PROFILE = 'profile',
  CHANGE_PASSWORD = 'password',
}

const initialChangePasswordState = {
  old_password: '',
  password: '',
  password_confirmation: '',
};

export const useUserSettings = () => {
  //store
  const dispatch = useDispatch();
  const clearErrorMessage = bindActionCreators(requestClearError, dispatch);

  //hooks
  const { currentUser } = useUser();
  const { currentProvider } = useProvider();
  const { navigate, getSearchParams } = useNavigation();
  const { requestWithCallback } = useRequest();
  const { t } = useTranslation();
  const { showToast } = useToastMessage((toastId: string) => clearErrorMessage(toastId));
  const { updateUser, updatingError } = useRequestUser();

  //state
  const initialProfileState = {
    first_name: currentUser?.first_name || '',
    last_name: currentUser?.last_name || '',
    email: currentUser?.email || '',
  };
  const [activeSection, setActiveSection] = useState<SettingSectionEnum>(
    SettingSectionEnum.USER_PROFILE,
  );
  const [profileValidationErrors, setProfileValidationErrors] = useState<IValidationErrors[]>([]);
  const [profileInitialValues, setProfileInitialValues] = useState<Partial<IUser>>(
    initialProfileState,
  );
  const [changePasswordInitialValues, setChangePasswordInitialValues] = useState<IChangePassword>(
    initialChangePasswordState,
  );
  const latestFamsChangeRequest = useMemo<jsonpatch.Operation[]>(
    //compare initial values and the state
    () => {
      const currentUserObject = {
        first_name: currentUser?.first_name,
        last_name: currentUser?.last_name,
        email: currentUser?.email,
      };
      return jsonpatch
        .compare(currentUserObject, profileInitialValues)
        .map((change: any) => ({ ...change, path: `/user${change.path}` }));
    },
    [profileInitialValues, currentUser],
  );

  //Call to actions
  const onUserSettingsUpdate = () => {
    switch (activeSection) {
      case SettingSectionEnum.USER_PROFILE:
        onProfileUpdate();
        break;
      case SettingSectionEnum.CHANGE_PASSWORD:
        onPasswordChange();
        break;
      default:
        break;
    }
  };

  const onProfilePictureUpdate = async (imageData: string) => {
    await setProfilePicture(
      requestWithCallback,
      { imageData, name: currentUser?.uuid },
      currentProvider?.domain || '',
      (response: Partial<IProviderProfilePicture>) => {
        updateUser([
          { op: 'replace', path: '/user/image_id', value: response.id },
          { op: 'replace', path: '/user/image_url', value: response.url },
        ]);
      },
      (error) => {
        error?.message && showToast('UpdateProfileError', error.message, ToastVariant.ERROR);
      },
    );
  };

  const onProfileUpdate = () => {
    if (latestFamsChangeRequest.length === 0 || validateEmail(profileInitialValues.email)) return;
    updateUser(latestFamsChangeRequest);
  };

  const onPasswordChange = () => {
    if (!isChangePasswordFormValid) return;
    changePassword(
      requestWithCallback,
      { user: changePasswordInitialValues },
      () => {
        //this is where we need to invalidate the user token
        showToast(
          'ChangePasswordSuccess',
          t('app.user.changePassword.success'),
          ToastVariant.SUCCESS,
        );
        logout();
      },
      (error) => {
        error?.message && showToast('ChangePasswordError', error.message, ToastVariant.ERROR);
      },
    );
  };

  //use memos
  const isChangePasswordFormValid = useMemo(() => {
    const { old_password, password, password_confirmation } = changePasswordInitialValues;
    return Boolean(old_password) && Boolean(password) && Boolean(password_confirmation);
  }, [changePasswordInitialValues]);

  const actionDisabled = useMemo(() => {
    switch (activeSection) {
      case SettingSectionEnum.USER_PROFILE:
        return latestFamsChangeRequest.length === 0;
      case SettingSectionEnum.CHANGE_PASSWORD:
        return !isChangePasswordFormValid;
      default:
        return;
    }
  }, [latestFamsChangeRequest, isChangePasswordFormValid, activeSection]);

  const settingTitle = useMemo(() => {
    switch (activeSection) {
      case SettingSectionEnum.USER_PROFILE:
        return t('app.user.profile');
      case SettingSectionEnum.CHANGE_PASSWORD:
        return t('app.user.changePassword');
      default:
        return t('app.user.settings');
    }
  }, [activeSection]);

  //life cycles
  useEffect(() => {
    //reset state each time section changed
    setProfileInitialValues(initialProfileState);
    setChangePasswordInitialValues(initialChangePasswordState);
  }, [activeSection]);

  useEffect(() => {
    const { section } = getSearchParams();
    setActiveSection(
      section === SettingSectionEnum.CHANGE_PASSWORD
        ? SettingSectionEnum.CHANGE_PASSWORD
        : SettingSectionEnum.USER_PROFILE,
    );
  }, [getSearchParams]);

  return {
    onUserSettingsUpdate,
    profileInitialValues,
    latestFamsChangeRequest,
    profileValidationErrors,
    setProfileValidationErrors,
    setProfileInitialValues,
    setChangePasswordInitialValues,
    changePasswordInitialValues,
    actionDisabled,
    navigate,
    settingTitle,
    activeSection,
    setActiveSection,
    t,
    onProfilePictureUpdate,
    currentUser,
    updatingError,
  };
};
